<template>
  <div class="h-full w-full relative">
    <EditorDeckSlidesEditorOverlay :show-content="showPdfReplace">
      <template #top-bar>
        <div class="space-y-4">
          <div class="flex justify-between items-center">
            <div class="max-w-md">
              <p class="mb-1.5 truncate">
                Source name:&nbsp;
                <VLink class="text-copper-300" component="a" font-weight="normal" :href="slide.file?.url" target="_blank">
                  {{ slide.file?.name }}
                </VLink>
              </p>
              <p class="mb-0">Source page: {{ slide.image?.page_number }}</p>
            </div>
            <VButton
              v-if="!showPdfReplace"
              ref="replacePdfButtonRef"
              class="flex items-center"
              size="xs"
              @click="$emit('update:showPdfReplace', true)"
            >
              <ArrowRotate class="w-3 mr-2" />
              Replace Slide
            </VButton>
            <button
              v-else
              class="appearance-none p-0 border-0 bg-transparent transition-opacity hover_opacity-80"
              type="button"
              @click="$emit('update:showPdfReplace', false)"
            >
              <XMarkIcon class="w-6" />
            </button>
          </div>

          <VMessage
            v-if="!isHighResPdfAvailable"
            class="items-center"
            :show="true"
            banner-type="warning"
            data-testid="editor-slide-inactive"
          >
            <template #icon>
              <div class="text-gray-900">
                <ArrowPathIcon class="w-5 h-5 mr-2" style="transform: scaleX(-1)" />
              </div>
            </template>

            Enhancing PDF quality – your high-resolution version is on the way. This may take several minutes.

            <template #dismiss>
              <VButton size="xs" class="ml-4" @click="refetchFile"> Refresh </VButton>
            </template>
          </VMessage>
        </div>
      </template>

      <template #content>
        <DeckPdfSlide :slide="slide" />
      </template>

      <template #overlay>
        <div class="grid place-items-center h-full">
          <EditorLocalOrServerChoice
            ref="localOrServerChoiceContainerRef"
            :company-id="companyId"
            :folder-id="folderId"
            :max-resolution="[4096, 4096]"
            file-type="PDF"
            mime-type="application/pdf"
            @choose-file="chooseFiles($event)"
            @goto:shelf="activeDialog = 'choose-file'"
            @error="$emit('error', $event)"
          />
        </div>
      </template>
    </EditorDeckSlidesEditorOverlay>

    <VMultiModal :showing="!!activeDialog">
      <EditorDeckModalsShelfSelector
        v-if="activeDialog === 'choose-file'"
        key="choose-file"
        :folder-id="folderId"
        :company-id="companyId"
        showing
        action="update"
        file-type="PDF"
        mime-type="application/pdf"
        @choose-file="chooseFiles($event)"
        @close="close"
        @back="close"
      />
      <EditorDeckModalsSelectPages
        v-if="activeDialog === 'select-page'"
        key="select-page"
        showing
        action="update"
        :pdf-file="selectedFile"
        @close="close"
        @choose-pages="choosePages($event)"
        @back="activeDialog = 'choose-file'"
      />
    </VMultiModal>
  </div>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { ref, onUnmounted, computed } from 'vue'
import { XMarkIcon, ArrowPathIcon } from '@heroicons/vue/24/outline'

import type { Slide } from '@/entities/deck'
import type { ShelfFile } from '@/entities/shelfFile'
import ArrowRotate from '@/components/icons/ArrowRotate.svg'

const props = defineProps({
  slide: {
    type: Object as PropType<DeepReadonly<Slide>>,
    required: true,
  },
  folderId: {
    type: Number,
    default: null,
  },
  companyId: {
    type: Number,
    required: true,
  },
  showPdfReplace: {
    type: Boolean,
    required: true,
  },
  slideRefreshKey: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['update:showPdfReplace', 'update:slide', 'refetch-file', 'error'])

const activeDialog = ref<'' | 'choose-file' | 'select-page'>('')
const selectedFile = ref<ShelfFile | null>(null)
const replacePdfButtonRef = ref<any | undefined>()
const localOrServerChoiceContainerRef = ref<any | undefined>()
const isHighResPdfAvailable = computed(() => props.slide.image?.id)

useClickOutside(
  [computed(() => replacePdfButtonRef.value?.$el), computed(() => localOrServerChoiceContainerRef.value?.$el)],
  computed(() => !!activeDialog.value),
  close
)

onUnmounted(() => {
  close()
})

function chooseFiles(file: ShelfFile) {
  selectedFile.value = file
  activeDialog.value = 'select-page'
}

function choosePages(pages: number[]) {
  if (!selectedFile.value || !pages.length) {
    return
  }

  emit('update:slide', {
    id: props.slide.id,
    file_id: selectedFile.value.id,
    page_number: pages[0],
  })

  close()
}

function close() {
  activeDialog.value = ''
  selectedFile.value = null
  emit('update:showPdfReplace', false)
}

function refetchFile() {
  emit('refetch-file')
}
</script>
